import { FilterChip } from 'components/UI/Responsive/FilterChips';
import { DateTypeFilterEnum } from 'enums/dateTypeFilter';
import { SaleTypeFilterEnum } from 'enums/saleTypeFilter';
import FormFiltersFields from 'model/FormFiltersFields';
import moment from 'moment';

export const getMyOrdersFilterInitialValues = () => ({
  'Data.Date': moment(),
  'Data.TypeDate': DateTypeFilterEnum.BUY_DATE,
  'Data.SaleTypes': SaleTypeFilterEnum.ALL,
  'Data.Status': '',
});

export const MY_ORDERS_ADVANCED_FIELDS = [
  'Data.OrderNumber',
  'Data.SiteTradingName',
  'Data.ProductDescription',
  'Data.UserName',
] as const;

export const getClearedFilterValues = () => ({
  ...getMyOrdersFilterInitialValues(),
  'Data.OrderNumber': undefined,
  'Data.SiteTradingName': undefined,
  'Data.ProductDescription': undefined,
  'Data.UserName': '',
});

const isFilled = (value: unknown) =>
  value !== undefined && value !== null && String(value).trim() !== '';

export const countActiveAdvancedFilters = (
  values: Partial<FormFiltersFields>,
): number =>
  MY_ORDERS_ADVANCED_FIELDS.filter((field) => isFilled(values[field])).length;

const DATE_TYPE_LABELS: Record<string, string> = {
  [DateTypeFilterEnum.BUY_DATE]: 'Compra',
  [DateTypeFilterEnum.DELIVERY_DATE]: 'Entrega',
  [DateTypeFilterEnum.BILLING_DATE]: 'Faturamento',
};

const SALE_TYPE_LABELS: Record<string, string> = {
  [SaleTypeFilterEnum.DIRECTED]: 'Direcionada',
  [SaleTypeFilterEnum.VOL]: 'ENP',
  [SaleTypeFilterEnum.LKP]: 'LKP',
};

const STATUS_LABELS: Record<string, string> = {
  F: 'Gerado',
  E: 'Não gerado',
  A: 'Aberto',
  R: 'Processando',
  P: 'Programado',
  C: 'Cancelado',
};

export const describeAppliedPeriod = (
  values: Partial<FormFiltersFields>,
): string | undefined => {
  const date = values['Data.Date'];
  if (!date) return undefined;
  const typeLabel = DATE_TYPE_LABELS[values['Data.TypeDate'] ?? ''] ?? 'Data';
  return `${typeLabel} a partir de ${moment(date).format('DD/MM/YYYY')}`;
};

type ChipField = keyof FormFiltersFields;

export const buildMyOrdersFilterChips = (
  values: Partial<FormFiltersFields>,
  onRemove: (field: ChipField) => void,
): FilterChip[] => {
  const chips: FilterChip[] = [];
  const period = describeAppliedPeriod(values);
  if (period) {
    chips.push({
      key: 'period',
      name: 'Período',
      text: period,
      highlighted: true,
    });
  }

  const saleType = values['Data.SaleTypes'];
  if (saleType && saleType !== SaleTypeFilterEnum.ALL) {
    chips.push({
      key: 'saleType',
      name: 'Tipo de compra',
      text: SALE_TYPE_LABELS[saleType] ?? saleType,
      onRemove: () => onRemove('Data.SaleTypes'),
    });
  }

  const status = values['Data.Status'];
  if (isFilled(status)) {
    chips.push({
      key: 'status',
      name: 'Status',
      text: STATUS_LABELS[status as string] ?? String(status),
      onRemove: () => onRemove('Data.Status'),
    });
  }

  const advanced: Array<[ChipField, string]> = [
    ['Data.OrderNumber', 'Pedido'],
    ['Data.SiteTradingName', 'Produtor'],
    ['Data.ProductDescription', 'Produto'],
    ['Data.UserName', 'Usuário'],
  ];
  advanced.forEach(([field, name]) => {
    if (isFilled(values[field])) {
      chips.push({
        key: field,
        name,
        text: `${name}: ${values[field]}`,
        onRemove: () => onRemove(field),
      });
    }
  });

  return chips;
};
